// Shared Official (Watts) MCO data loaders — used by both the Add Plan combo builder
// and the useMco Details-dialog hook, so both read the exact same actuarial numbers.

import { MCO_INPUT_LEGS, summarizePriceDiscoveryLeg } from "./mcoPaymentLedger.js";
import { num, pickRow, resolveMcoPracticeName } from "./mcoShared.js";

export { pickRow };

/** Watts GetPractices is Irrigated / Non-Irrigated only — collapse farm Fac/Nfac labels. */
function withWattsPractice(common) {
  const practice_name = resolveMcoPracticeName(common?.practice_name) || common?.practice_name;
  return { ...common, practice_name };
}

/** AreaLoss row → fractional coverage start / area-loss end + the order code the band is keyed by. */
export function areaLossBandsFromRows(rows) {
  return (rows || []).map((row) => {
    const order = pickRow(row, "arealoss", "ddarealoss", "order", "arealossorder") ?? "1";
    let start = Number(pickRow(row, "arealossstartpercent", "startpercent") ?? 0.95);
    let end = Number(pickRow(row, "arealossendpercent", "endpercent") ?? 0.86);
    if (start > 1) start /= 100;
    if (end > 1) end /= 100;
    return { order: String(order), start, end };
  });
}

/** Flatten Official Actuarial's nested mcoActuarialData / mcoPremiumData onto the row. */
function flattenActuarial(actRaw) {
  const actData = pickRow(actRaw, "mcoactuarialdata") || {};
  const premData = pickRow(actRaw, "mcopremiumdata") || {};
  return { ...(actRaw || {}), ...actData, ...premData };
}

/**
 * One Actuarial row + the crop-leg PriceDiscovery row — the minimum needed to price an
 * MCO cell (EM/ER/costs + projected/harvest crop price). Used per band while building
 * Add Plan combo rows.
 *
 * @param {object} mcoService — MCOService().actions
 * @param {object} common — geo names + { insurance_plan_code, approved_yield, area_loss_order, area_loss_start_percent, area_loss_end_percent, protection_factor }
 */
export async function loadMcoActuarialAndCropLeg(mcoService, common) {
  const watts = withWattsPractice(common);
  const [actRows, cropPdRows] = await Promise.all([
    mcoService.selectOfficial("Actuarial", watts),
    mcoService.selectOfficial("PriceDiscovery", { ...watts, input_cost_type: 1 }),
  ]);
  const actuarial = flattenActuarial(actRows?.[0] || null);
  const cropPd = cropPdRows?.[0] || null;
  const cropLeg = summarizePriceDiscoveryLeg(cropPd, "Crop");
  return { actuarial, cropPd, cropLeg };
}

/**
 * Full ledger bundle (all 6 input-cost legs) — used by the Details dialog's MCO Input
 * Costs accordion and payment formula rows.
 */
export async function loadMcoLedgerBundle(mcoService, common) {
  const watts = withWattsPractice(common);
  const [actRows, ...legPdLists] = await Promise.all([
    mcoService.selectOfficial("Actuarial", watts),
    ...MCO_INPUT_LEGS.map((leg) =>
      mcoService.selectOfficial("PriceDiscovery", { ...watts, input_cost_type: leg.type })
    ),
  ]);
  const legs = MCO_INPUT_LEGS.map((leg, i) => summarizePriceDiscoveryLeg(legPdLists[i]?.[0] || null, leg.name));
  const actuarial = flattenActuarial(actRows?.[0] || null);
  const cropPd = legPdLists[0]?.[0] || null;
  return { actuarial, legs, cropPd };
}

/**
 * Actuarial + crop-leg → the `computeMcoProjectedPayment` baseline shape (missing only
 * finalYield/harvestPrice, filled in per-cell by callers). Shared so buildMcoComboRows and
 * useMco compute the exact same Official-derived numbers.
 *
 * @param {object} actuarial — flattened Actuarial row (see loadMcoActuarialAndCropLeg)
 * @param {object} cropLeg — summarizePriceDiscoveryLeg(cropPd, "Crop") result
 * @param {object} farmer — { approvedYield, planCode, coverageStart, areaLossEnd, protectionFactor, sharePct, acres }
 */
export function paymentBaseFromActuarial(actuarial, cropLeg, farmer) {
  const officialEay = num(actuarial?.expectedindexvalue ?? actuarial?.expectedareayield);
  const expectedYield = officialEay || num(farmer.approvedYield);
  // Which of the yield sources this is, so views that bridge to $/ac can disclose the basis
  // (app_implementation_guide.md §4.4: RMA publishes no EAY until ~3 business days after the
  // discovery window closes, so in-window per-acre figures rest on an estimate).
  const expectedYieldBasis = officialEay ? "official" : "approved_yield";
  const projectedCosts = num(actuarial?.expectedcosts);
  const harvestCostsRaw = actuarial?.harvestcosts;
  const harvestCostsHeldAtExpected = harvestCostsRaw == null || harvestCostsRaw === "";
  const harvestCosts = harvestCostsHeldAtExpected
    ? Number.isFinite(projectedCosts)
      ? projectedCosts
      : null
    : num(harvestCostsRaw);
  const expectedRevenue = actuarial?.expectedrevenueamount != null ? num(actuarial.expectedrevenueamount) : null;
  const expectedMargin = actuarial?.expectedmarginamount != null ? num(actuarial.expectedmarginamount) : null;
  const officialTriggerMargin = actuarial?.triggermargin != null ? num(actuarial.triggermargin) : null;

  return {
    expectedYield,
    expectedYieldBasis,
    projectedPrice: num(cropLeg?.projectedPrice),
    expectedRevenue,
    expectedMargin,
    officialTriggerMargin,
    projectedCosts,
    harvestCosts,
    harvestCostsHeldAtExpected,
    approvedYield: num(farmer.approvedYield),
    planCode: farmer.planCode,
    coverageStart: farmer.coverageStart,
    areaLossEnd: farmer.areaLossEnd,
    protectionFactor: num(farmer.protectionFactor ?? 1),
    sharePct: farmer.sharePct,
    acres: num(farmer.acres),
  };
}

/**
 * Official AreaLoss band offers — pass names; aip-pass Official resolves to Watts codes.
 * @param {object} mcoService — MCOService().actions
 * @param {object} geo — { crop_year|reinsurance_year, commodity_name, state_name?, county_name? }
 */
export async function loadMcoAreaLossBands(mcoService, geo) {
  const crop_year = geo?.crop_year || geo?.reinsurance_year;
  if (!crop_year || !geo?.commodity_name) return [];
  const rows = await mcoService.selectOfficial("AreaLoss", {
    crop_year,
    commodity_name: geo.commodity_name,
    state_name: geo.state_name,
    county_name: geo.county_name,
  });
  return areaLossBandsFromRows(rows);
}
